// staffCounters.js
// Pure derivation of per-staff workload counters over a set of dates.
//
// Scope decisions (see conversation):
//  - Visibility aid, NOT a compliance check. No thresholds/limits here.
//  - Counts one staff member across ALL locations they worked (no split by
//    institution). One row -> one set of numbers.
//  - Both metrics are ALWAYS computed; the UI displays whichever the global
//    toggle selects. This keeps future per-department granularity a display
//    change, never a recompute.
//  - Weekday (Mon-Fri) vs weekend (Sat/Sun) split for both metrics.
//  - A third metric, `days` (distinct dates worked), was removed in Sep 2026 at
//    the department's request. NOTE this was the DISPLAY counter only --
//    workload_limit's own `unit: days`, which IP's weekday/weekend day cap runs
//    on, is a separate thing in the solver and is untouched.
//
// On-call handling: on-call items have no start/end span (only a date), so they
// contribute to the SHIFT count but not to HOURS. Clinical shifts carry
// start/end datetimes and drive the hours metric. Centralised here so it's easy
// to change (e.g. assign on-call a nominal duration) in one place.
//
// Cross-cover handling: two clinical items with the same hours and shift type on
// the same date (one person covering two wards) are ONE block of work — one
// shift, one span of hours. They are collapsed via services/crossCover.js so
// these counters agree with the solver's work-block counting and the Excel tally.

import { dedupeBlocks } from './crossCover';
import { partOn } from './leave';

const isWeekendISO = (iso) => {
  // iso is a YYYY-MM-DD date string; parse as local midnight to avoid TZ drift.
  const d = new Date(`${iso}T00:00:00`);
  const day = d.getDay();           // 0 = Sun, 6 = Sat
  return day === 0 || day === 6;
};

// Date portion of an item, whether clinical (datetime start) or on-call (date start).
const itemDateISO = (item) => {
  // Guard the item itself: the `others` bucket below keeps anything that is not
  // a named clinical shift, which includes a null or undefined entry. These run
  // on every render of the Final Roster, so one bad entry in the list took the
  // whole tab down with a TypeError rather than being skipped.
  if (!item) return '';
  const s = item.start;
  return typeof s === 'string' ? s.split('T')[0] : '';
};

// Duration of a clinical shift in hours from start/end datetimes. Returns 0 if
// either bound is missing or unparseable (on-call, or malformed data).
const shiftHours = (item) => {
  if (!item || !item.start || !item.end) return 0;
  const start = new Date(item.start);
  const end = new Date(item.end);
  const ms = end - start;
  if (!Number.isFinite(ms) || ms <= 0) return 0;
  return ms / (1000 * 60 * 60);
};

/**
 * How much of each in-window day a person is on leave for, from their leave
 * map ({date: "full" | "AM" | "PM"}, services/leave.js).
 *
 * @returns {Map<string, number>} ISO date -> 1 (full) or 0.5 (half)
 */
export function leaveDayFractions(leave, windowDates) {
  const person = { leave: leave || {} };
  const out = new Map();
  (windowDates || []).forEach((iso) => {
    const part = partOn(person, iso);
    if (part) out.set(iso, part === 'full' ? 1 : 0.5);
  });
  return out;
}

/**
 * What a person's leave contributes over the window, split weekday/weekend, at
 * `valuePerDay` per full day (half that for a half day).
 *
 * Unit-agnostic on purpose: the shifts and hours counters each carry their own
 * toggle and their own value, and both apply the same fraction rule, so one
 * function serves both rather than two that could drift apart.
 *
 * Display only — see DataService.get_leave_counter_settings for why this never
 * reaches the solver.
 */
export function leaveContribution(leave, windowDates, valuePerDay) {
  const out = { weekday: 0, weekend: 0, total: 0 };
  const value = Number(valuePerDay);
  if (!Number.isFinite(value) || value <= 0) return out;
  leaveDayFractions(leave, windowDates).forEach((fraction, iso) => {
    const bucket = isWeekendISO(iso) ? 'weekend' : 'weekday';
    const amount = fraction * value;
    out[bucket] += amount;
    out.total += amount;
  });
  return out;
}

/**
 * Compute a staff member's counters over the given window.
 *
 * @param {Array} items   pivoted items for ONE person: { type:'clinical'|'oncall', start, end?, ... }
 * @param {Array<string>} windowDates  ISO date strings (YYYY-MM-DD) defining the visible window
 * @param {{leave?: Object<string,string>, leaveShiftsValue?: number,
 *           leaveHoursValue?: number}} [options]
 *        Each in-window leave day in `leave` adds `leaveShiftsValue` to the
 *        SHIFTS metric and `leaveHoursValue` to the HOURS metric; a half-day
 *        block adds half of each. The two are INDEPENDENT — a zero (or omitted)
 *        value means that metric ignores leave, which is how the UI expresses
 *        "toggle off". With neither set, every existing caller keeps its current
 *        numbers exactly.
 *
 *        Both are always applied, not just the displayed one, so switching the
 *        metric is a display change and never a recompute — the same principle
 *        the shifts/hours split already follows.
 *
 *        The solver reads leave the OPPOSITE way (workload_limit.leave_reduces
 *        LOWERS a cap by each leave day), so this is a display aid and nothing
 *        more.
 * @returns {{ shifts: {weekday:number, weekend:number, total:number},
 *             hours:  {weekday:number, weekend:number, total:number},
 *             leaveShifts: {weekday:number, weekend:number, total:number},
 *             leaveHours:  {weekday:number, weekend:number, total:number} }}
 */
export function computeStaffCounters(items, windowDates, options = {}) {
  const inWindow = new Set(windowDates || []);
  const out = {
    shifts: { weekday: 0, weekend: 0, total: 0 },
    hours: { weekday: 0, weekend: 0, total: 0 },
  };

  // One entry per work block: a cross-cover (two wards, same hours) counts once.
  // On-call items carry no end and keep their own identity through the dedupe.
  const clinical = (items || []).filter((i) => i && i.type === 'clinical' && i.employee?.id);
  const others = (items || []).filter((i) => !(i && i.type === 'clinical' && i.employee?.id));
  const blocks = [...dedupeBlocks(clinical), ...others];

  blocks.forEach((item) => {
    const iso = itemDateISO(item);
    if (!iso || !inWindow.has(iso)) return;   // only count within the visible window

    const weekend = isWeekendISO(iso);
    const bucket = weekend ? 'weekend' : 'weekday';

    // Every item (clinical or on-call) is one shift.
    out.shifts[bucket] += 1;
    out.shifts.total += 1;

    // Only clinical items with a real span contribute hours.
    const hrs = shiftHours(item);
    if (hrs > 0) {
      out.hours[bucket] += hrs;
      out.hours.total += hrs;
    }
  });

  // Leave, per metric, each under its own toggle (a zero value = off). Kept
  // separately as well as folded in, so the UI can say how much of a total came
  // from leave rather than presenting one opaque number.
  const lvShifts = leaveContribution(options.leave, windowDates, options.leaveShiftsValue);
  const lvHours = leaveContribution(options.leave, windowDates, options.leaveHoursValue);
  ['weekday', 'weekend', 'total'].forEach((k) => {
    out.shifts[k] += lvShifts[k];
    out.hours[k] += lvHours[k];
  });

  // One decimal, for display stability. Shifts can now be fractional too — a
  // half-day of leave at 1 shift/day is half a shift — but a whole number must
  // stay whole: a shifts column of "5" should not read "5.0" merely because the
  // leave feature exists. round1 gives that for free (Math.round(50)/10 === 5).
  const round1 = (n) => Math.round(n * 10) / 10;
  const round3 = (o) => ({ weekday: round1(o.weekday), weekend: round1(o.weekend), total: round1(o.total) });
  out.shifts = round3(out.shifts);
  out.hours = round3(out.hours);
  out.leaveShifts = round3(lvShifts);
  out.leaveHours = round3(lvHours);

  return out;
}
